import React from 'react';
import { Chip, Tooltip } from '@material-ui/core';
import { amber, green, grey, red } from '@material-ui/core/colors';
import Currency from './Currency';
import {
    MATCHING_FULL,
    MATCHING_INCONSISTENT,
    MATCHING_PARTIAL,
    matchTotal,
    matchingState,
    remainingAmount,
} from '../../../../jsx/utils/factId5000';

// Un fond ambre sur la ligne signalait jusqu'ici la seule présence de fact_id_5000 :
// une opération à qui il restait 3 FCFA à pointer était donc présentée comme soldée.
// L'état réel se lit sur total_available / matched, et il a trois valeurs utiles plus
// un cas de données incohérentes qu'aucun écran ne doit trancher à la place du comptable.
const PRESENTATION = {
    [MATCHING_FULL]: { label: 'Pointé', color: green[700], background: green[50] },
    [MATCHING_PARTIAL]: { label: 'Partiel', color: amber[900], background: amber[50] },
    [MATCHING_INCONSISTENT]: { label: 'Incohérent', color: red[700], background: red[50] },
};

const NONE = { label: 'Non pointé', color: grey[700], background: 'transparent' };

/** Couleur de fond de ligne d'une opération, réservée aux états sur lesquels elle informe. */
export function matchingRowColor(operation) {
    switch (matchingState(operation)) {
        case MATCHING_FULL:
            return amber[50];
        case MATCHING_INCONSISTENT:
            return red[50];
        default:
            // Partiellement pointée ou pas pointée du tout : dans les deux cas il reste du
            // travail, et surligner la ligne laisserait croire le contraire.
            return 'initial';
    }
}

/**
 * Pastille d'état du rapprochement 5000, indépendante de ce qui est rapproché.
 *
 * Partagée par les opérations de stock (MatchingStatus) et les paiements PAYGo
 * (PaymentMatchingStatus) : les deux écrans doivent se lire de la même façon.
 * `remainingLabel` est le reste déjà formaté dans la devise de l'appelant ; `totalLabel`,
 * le montant de référence, n'est affiché que dans l'infobulle.
 */
export function MatchingChip({ state, remainingLabel, totalLabel, fullTitle, noneTitle, detail }) {
    const { label, color, background } = PRESENTATION[state] || NONE;
    const hasRemaining = state === MATCHING_PARTIAL && remainingLabel !== null && remainingLabel !== undefined;

    let title;
    if (hasRemaining) {
        title = totalLabel ? `Reste à pointer sur un montant de ${totalLabel}` : 'Reste à pointer';
    } else if (state === MATCHING_INCONSISTENT) {
        title = "Référence 5000 renseignée sans reste à pointer cohérent : à vérifier côté comptabilité avant tout nouveau pointage";
    } else if (state === MATCHING_FULL) {
        title = fullTitle || 'Intégralement rapprochée avec la comptabilité 5000';
    } else {
        title = noneTitle || "Aucun rattachement à la comptabilité 5000";
    }

    const chip = (
        <Chip
            size="small"
            variant="outlined"
            label={hasRemaining ? <span>{label} — reste {remainingLabel}</span> : label}
            style={{ color, borderColor: color, backgroundColor: background }}
        />
    );

    return <Tooltip title={detail ? <span>{title}{detail}</span> : title}>{chip}</Tooltip>;
}

/**
 * Pastille d'état du rapprochement 5000 d'une vente, d'un achat ou d'un réappro externe.
 *
 * Le montant restant est porté par l'infobulle plutôt que par le libellé : il tient
 * rarement dans une colonne, et c'est une information de contrôle, pas de tri.
 */
function MatchingStatus({ operation, currency }) {
    const state = matchingState(operation);
    const remaining = remainingAmount(operation);
    const total = matchTotal(operation);

    return (
        <MatchingChip
            state={state}
            remainingLabel={remaining !== null ? <Currency value={remaining} sourceCurrency={currency} /> : null}
            totalLabel={total ? total.toLocaleString('fr-FR') : null}
        />
    );
}

export default MatchingStatus;
